import { FilterLines, SearchLg } from '@untitledui/icons';
import type { ReactNode, RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Badge } from '@/lib/base/badges/badges';
import { InputBase, TextField } from '@/lib/base/forms/input/input';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

export interface ToolbarProps {
  children: ReactNode;
  /** Show a bottom border below the toolbar (default: true). Set to false when
   *  the content below already provides its own top border (e.g., tables). */
  borderBottom?: boolean;
  /** Additional className on the toolbar container */
  className?: string;
}

/**
 * Toolbar — shared horizontal bar rendered below the page header.
 *
 * Provides consistent padding, gap, and border styling across all v2 pages
 * (CRM list, pipeline, calendar, reports). Individual views should NOT set
 * their own toolbar padding — use this component instead.
 *
 * Designed to be rendered inside a container with `px-4`. Uses negative
 * horizontal margin (`-mx-4`) to extend borders full-width, then re-applies
 * `px-4` for content alignment.
 */
export function Toolbar({
  children,
  borderBottom = true,
  className,
}: ToolbarProps) {
  return (
    <div
      className={cx(
        'flex items-center gap-6 py-3 px-4 -mx-4',
        'border-t border-[var(--color-border-secondary)]',
        borderBottom && 'border-b',
        className
      )}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ToolbarSection
// ---------------------------------------------------------------------------

export interface ToolbarSectionProps {
  children: ReactNode;
  /** Additional className */
  className?: string;
}

/**
 * ToolbarSection — groups related toolbar actions with consistent internal spacing.
 *
 * Use this to group items like Search + Filter + Chips, or Sort + View toggle,
 * so they share the same gap regardless of which view renders them.
 */
export function ToolbarSection({ children, className }: ToolbarSectionProps) {
  return (
    <div className={cx('flex items-center gap-4', className)}>{children}</div>
  );
}

// ---------------------------------------------------------------------------
// ToolbarSearch
// ---------------------------------------------------------------------------

export interface ToolbarSearchProps {
  /** Current search value (controlled) */
  value: string;
  /** Called when the search value changes (debounced by the consumer) */
  onChange: (value: string) => void;
  /** Placeholder text when expanded */
  placeholder?: string;
  /** Ref forwarded to the inner <input> element */
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Called when the input loses focus */
  onBlur?: () => void;
  /** Called when the input gains focus */
  onFocus?: () => void;
  /** Controlled expanded state. When provided, the consumer manages
   *  expand/collapse (e.g., for typeahead that needs delayed collapse). */
  expanded?: boolean;
  /** Called when the search wants to expand (button click) or collapse (blur
   *  with empty value). Only used when `expanded` is provided. */
  onExpandedChange?: (expanded: boolean) => void;
  /** Extra props spread onto the inner InputBase (e.g., aria-* for combobox,
   *  onKeyDown for keyboard navigation). */
  inputProps?: React.AriaAttributes & {
    role?: string;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  };
  /** Render custom content below the input (e.g., typeahead dropdown). */
  children?: ReactNode;
  /**
   * When true, renders `data-state="searching"` (overrides expanded/collapsed),
   * allowing e2e tests to wait deterministically for the debounce to settle via
   * the `searchBar().search()` helper. Callers that omit this prop cause
   * `searchBar().search()` to fall back to a fixed 500 ms wait instead of a
   * state-based wait. Wire this prop in any component whose e2e tests call
   * `searchBar().search()`.
   */
  isSearching?: boolean;
}

// ToolbarSearch state precedence (e2e contract):
//   searching > expanded > collapsed
// The data-state attribute is rendered on the wrapper element (button when
// collapsed, div when expanded), not on the inner <input>. The helper
// queries [data-component="search-bar"] to locate the bar.

/**
 * ToolbarSearch — shared collapsed/expanded search input for v2 toolbars.
 *
 * Collapsed: icon + "Search" text button (matches CRM pattern).
 * Expanded: InputBase with search icon, consistent sizing and focus ring.
 *
 * **Uncontrolled** (default): manages expand/collapse internally.
 * **Controlled** (`expanded` + `onExpandedChange`): the consumer manages
 * expand/collapse — useful for typeahead dropdowns that need delayed blur.
 *
 * For typeahead (e.g., calendar), pass `children` to render a dropdown below
 * the input and use `inputProps` for combobox aria attributes.
 */
export function ToolbarSearch({
  value,
  onChange,
  placeholder = 'Search',
  inputRef: externalInputRef,
  onBlur,
  onFocus,
  expanded: controlledExpanded,
  onExpandedChange,
  inputProps,
  children,
  isSearching,
}: ToolbarSearchProps) {
  const isDesktop = useBreakpoint('sm');
  const [internalExpanded, setInternalExpanded] = useState(() => !!value);
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;

  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  let dataState: 'collapsed' | 'expanded' | 'searching' = isExpanded
    ? 'expanded'
    : 'collapsed';
  if (isSearching) {
    dataState = 'searching';
  }

  const setExpanded = useCallback(
    (next: boolean) => {
      if (isControlled) {
        onExpandedChange?.(next);
      } else {
        setInternalExpanded(next);
      }
    },
    [isControlled, onExpandedChange]
  );

  const handleExpand = useCallback(() => {
    setExpanded(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [setExpanded, inputRef]);

  const handleBlur = useCallback(() => {
    if (!value) {
      setExpanded(false);
    }
    onBlur?.();
  }, [value, onBlur, setExpanded]);

  if (!isExpanded) {
    return (
      <button
        type='button'
        onClick={handleExpand}
        aria-label='Search'
        aria-expanded={false}
        data-component='search-bar'
        data-state={dataState}
        className='inline-flex items-center gap-1.5 text-sm font-semibold text-fg-primary hover:text-fg-secondary transition-colors'
      >
        <SearchLg className='size-4 shrink-0' />
        {isDesktop && 'Search'}
      </button>
    );
  }

  return (
    <div
      className='relative w-64 -my-1.5'
      data-component='search-bar'
      data-state={dataState}
    >
      <TextField value={value} onChange={onChange}>
        <InputBase
          ref={inputRef}
          type='text'
          placeholder={placeholder}
          icon={SearchLg}
          // `xs` IS this field: 32px at desktop with `text-sm` and a `size-4`
          // icon. It was previously hand-built out of `size='sm'` plus
          // `h-8` + `!py-0` + `text-sm` + `!size-4` — the same four values the
          // size step already defines, minus the responsive behaviour. Only
          // the focus-ring colour override is genuinely local.
          size='xs'
          onBlur={handleBlur}
          onFocus={onFocus}
          wrapperClassName='focus-within:!ring-[var(--color-focus-ring)] focus-within:!border-[var(--color-border-brand)]'
          {...inputProps}
        />
      </TextField>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ToolbarFilter
// ---------------------------------------------------------------------------

export interface ToolbarFilterProps {
  /** Number of active filter conditions */
  activeCount: number;
  /** Click handler to open filter panel */
  onClick: () => void;
  /** Optional tooltip content when filters are active */
  tooltip?: ReactNode;
}

/**
 * ToolbarFilter — shared filter button for v2 toolbars.
 *
 * Inactive: icon + "Filter" text button (matches Search pattern).
 * Active: brand-colored text with badge count + optional tooltip.
 */
export function ToolbarFilter({
  activeCount,
  onClick,
  tooltip,
}: ToolbarFilterProps) {
  const isDesktop = useBreakpoint('sm');

  if (activeCount > 0) {
    const button = (
      <AriaButton
        onPress={onClick}
        aria-label={`Filter – ${activeCount} active`}
        className='inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-fg-brand-primary)] hover:text-fg-secondary transition-colors shrink-0 cursor-pointer'
      >
        <FilterLines className='size-4' />
        {isDesktop && 'Filter'}
        {/*
          `leading-[1rem]` pins the badge's line-height. The `text-xs` utility
          in this build sets only font-size (no paired line-height in @theme),
          so the badge would otherwise inherit the button's `text-sm` 20px
          line-height and render ~24px tall — taller than the 20px toolbar row,
          nudging the row height whenever a filter is active.
        */}
        <Badge
          type='pill-color'
          color='brand'
          size='sm'
          className='leading-[1rem]'
        >
          {activeCount}
        </Badge>
      </AriaButton>
    );

    if (tooltip) {
      return (
        <Tooltip title={tooltip} placement='bottom start' delay={200}>
          {button}
        </Tooltip>
      );
    }

    return button;
  }

  return (
    <AriaButton
      onPress={onClick}
      aria-label='Filter'
      className='inline-flex items-center gap-1.5 text-sm font-semibold text-fg-primary hover:text-fg-secondary transition-colors shrink-0 cursor-pointer'
    >
      <FilterLines className='size-4' />
      {isDesktop && 'Filter'}
    </AriaButton>
  );
}
